import { useEffect, useState } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router';
import {
  SquaresFour,
  CheckCircle,
  CurrencyDollar,
  BookOpen,
  ShieldCheck,
  Key,
  ListMagnifyingGlass,
  UsersThree,
  GearSix,
  Plus,
  ClockCounterClockwise,
  MagnifyingGlass,
} from '@phosphor-icons/react';

const navigationItems = [
  { label: 'Dashboard', path: '/dashboard', icon: SquaresFour },
  { label: 'Approvals', path: '/approvals', icon: CheckCircle },
  { label: 'Budgets', path: '/budgets', icon: CurrencyDollar },
  { label: 'Policies', path: '/policies', icon: BookOpen },
  { label: 'Security', path: '/security', icon: ShieldCheck },
  { label: 'API Keys', path: '/api-keys', icon: Key },
  { label: 'Traces', path: '/traces', icon: ListMagnifyingGlass },
  { label: 'Team', path: '/team', icon: UsersThree },
  { label: 'Settings', path: '/settings', icon: GearSix },
];

const actionItems = [
  { label: 'Create API Key', path: '/api-keys', icon: Plus },
  { label: 'View Pending Approvals', path: '/approvals', icon: ClockCounterClockwise },
  { label: 'Search Traces...', path: '/traces', icon: MagnifyingGlass },
];

export function CommandBar() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  function handleSelect(path: string) {
    setOpen(false);
    navigate(path);
  }

  if (!open) return null;

  return (
    <>
      <style>{cmdkStyles}</style>
      <div className="palaryn-cmdk-overlay" onClick={() => setOpen(false)}>
        <div className="palaryn-cmdk-positioner" onClick={(e) => e.stopPropagation()}>
          <Command className="palaryn-cmdk" label="Command Menu">
            <div className="palaryn-cmdk-input-wrapper">
              <MagnifyingGlass
                size={18}
                weight="duotone"
                className="palaryn-cmdk-input-icon"
              />
              <Command.Input
                className="palaryn-cmdk-input"
                placeholder="Type a command or search..."
              />
              <kbd className="palaryn-cmdk-kbd">ESC</kbd>
            </div>

            <Command.List className="palaryn-cmdk-list">
              <Command.Empty className="palaryn-cmdk-empty">
                No results found.
              </Command.Empty>

              <Command.Group
                heading="Navigation"
                className="palaryn-cmdk-group"
              >
                {navigationItems.map((item) => (
                  <Command.Item
                    key={item.path}
                    value={item.label}
                    onSelect={() => handleSelect(item.path)}
                    className="palaryn-cmdk-item"
                  >
                    <item.icon size={18} weight="duotone" />
                    <span>{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>

              <Command.Separator className="palaryn-cmdk-separator" />

              <Command.Group
                heading="Actions"
                className="palaryn-cmdk-group"
              >
                {actionItems.map((item) => (
                  <Command.Item
                    key={`action-${item.label}`}
                    value={item.label}
                    onSelect={() => handleSelect(item.path)}
                    className="palaryn-cmdk-item"
                  >
                    <item.icon size={18} weight="duotone" />
                    <span>{item.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            </Command.List>
          </Command>
        </div>
      </div>
    </>
  );
}

const cmdkStyles = `
  .palaryn-cmdk-overlay {
    position: fixed;
    inset: 0;
    z-index: 100;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding-top: 20vh;
    animation: palaryn-cmdk-fade-in 150ms ease-out;
  }

  @keyframes palaryn-cmdk-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes palaryn-cmdk-slide-in {
    from { opacity: 0; transform: scale(0.98) translateY(-4px); }
    to { opacity: 1; transform: scale(1) translateY(0); }
  }

  .palaryn-cmdk-positioner {
    width: 100%;
    max-width: 560px;
    margin: 0 16px;
  }

  .palaryn-cmdk {
    background: var(--surface-1);
    border: 1px solid var(--border);
    border-radius: var(--radius-xl);
    box-shadow: var(--shadow-lg), 0 0 0 1px rgba(0, 0, 0, 0.1);
    overflow: hidden;
    animation: palaryn-cmdk-slide-in 150ms ease-out;
  }

  .palaryn-cmdk-input-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 16px;
    border-bottom: 1px solid var(--border);
  }

  .palaryn-cmdk-input-icon {
    color: var(--text-tertiary);
    flex-shrink: 0;
  }

  .palaryn-cmdk-input {
    flex: 1;
    background: transparent;
    border: none;
    outline: none;
    color: var(--text-primary);
    font-size: 15px;
    font-family: var(--font-sans);
    caret-color: var(--accent-teal);
  }

  .palaryn-cmdk-input::placeholder {
    color: var(--text-tertiary);
  }

  .palaryn-cmdk-kbd {
    flex-shrink: 0;
    font-size: 11px;
    font-family: var(--font-sans);
    color: var(--text-tertiary);
    background: var(--surface-2);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 2px 6px;
    line-height: 1.4;
  }

  .palaryn-cmdk-list {
    max-height: 360px;
    overflow-y: auto;
    padding: 8px;
  }

  .palaryn-cmdk-empty {
    padding: 32px 16px;
    text-align: center;
    color: var(--text-tertiary);
    font-size: 14px;
  }

  .palaryn-cmdk-group [cmdk-group-heading] {
    padding: 8px 8px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--text-tertiary);
    user-select: none;
  }

  .palaryn-cmdk-separator {
    height: 1px;
    background: var(--border);
    margin: 4px 8px;
  }

  .palaryn-cmdk-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--radius-md);
    font-size: 14px;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background var(--transition-fast), color var(--transition-fast);
    border-left: 2px solid transparent;
    user-select: none;
  }

  .palaryn-cmdk-item[data-selected='true'] {
    background: var(--surface-2);
    color: var(--accent-teal);
    border-left-color: var(--accent-teal);
  }

  .palaryn-cmdk-item:active {
    background: var(--surface-3);
  }

  /* Scrollbar within command list */
  .palaryn-cmdk-list::-webkit-scrollbar {
    width: 4px;
  }

  .palaryn-cmdk-list::-webkit-scrollbar-track {
    background: transparent;
  }

  .palaryn-cmdk-list::-webkit-scrollbar-thumb {
    background: var(--border);
    border-radius: 2px;
  }
`;
